import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { type TripDto } from "@tc/shared";
import { Button, Card, Icon, StatusBadge, TRANSPORT_ICON, TRIP_STATUS_BADGE } from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { dateTone, STATUS_KEY, TONE_BG, TONE_COLOR } from "./status";

/** Transport-mode flags → the icon row (flight/train/coach), in a stable order. */
export function TransportIcons({ modes }: { modes: TripDto["transportModes"] }) {
  if (!modes) return null;
  const active = (["flight", "train", "coach", "hireCar"] as const).filter((m) => modes[m]);
  if (active.length === 0) return null;
  return (
    <span className="transport-icons">
      {active.map((m) => (
        <Icon key={m} name={TRANSPORT_ICON[m]} size={15} />
      ))}
    </span>
  );
}

/**
 * A single trip as a card, used on the club dashboard's "Your trips" grid. Clicking opens the
 * trip detail (`/trips/:id`). The footer counts down to the trip's travel start (the only date
 * we have until proposal deadlines land in Module 3), colour-coded by urgency.
 */
export function TripCard({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const tone = dateTone(trip.travelStartDate);
  const dest = [trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ");

  return (
    <Card hover className="trip-card" onClick={() => navigate(`/trips/${trip.id}`)} style={{ cursor: "pointer" }}>
      <div className="tc-head">
        <div>
          <span className="t-id mono">{trip.reference}</span>
          <div className="tc-match">{trip.matchOpponent ?? trip.sport ?? trip.reference}</div>
          {trip.competition && <div className="tc-comp">{trip.competition}</div>}
        </div>
        <StatusBadge status={TRIP_STATUS_BADGE[trip.status]} label={t(STATUS_KEY[trip.status])} />
      </div>
      <div className="tc-meta">
        {dest && (
          <span className="mi">
            <Icon name="building" size={14} />
            {dest}
          </span>
        )}
        {trip.travelStartDate && (
          <span className="mi">
            <Icon name="calendar" size={14} />
            {fmt.date(trip.travelStartDate)}
            {trip.travelEndDate && ` – ${fmt.date(trip.travelEndDate)}`}
          </span>
        )}
        {trip.paxCount != null && (
          <span className="mi">
            <Icon name="roster" size={14} />
            {trip.paxCount} {t("units.pax")}
          </span>
        )}
        <span className="mi">
          <TransportIcons modes={trip.transportModes} />
        </span>
      </div>
      <div className="tc-foot">
        {trip.travelStartDate ? (
          <span className="pill" style={{ color: TONE_COLOR[tone], background: TONE_BG[tone] }}>
            <Icon name="clock" size={13} />
            {fmt.relative(trip.travelStartDate)}
          </span>
        ) : (
          <span className="faint" style={{ fontSize: 13 }}>
            —
          </span>
        )}
        <Button variant="ghost" size="sm" iconRight="chevR">
          {t("trips.open")}
        </Button>
      </div>
    </Card>
  );
}
